import * as Actions from '@/redux/actions';
import { faChurch, faClockRotateLeft, faEnvelope, faHandHoldingDollar, faHeart, faIdCardClip, faPhone, faArrowUpRightFromSquare } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Box, Card, CardContent, CircularProgress, Divider, Paper, Skeleton, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography, Link } from '@mui/material';
import get from 'lodash/get';
import moment from 'moment';
import { useCallback, useEffect, useState } from 'react';
import { shallowEqual } from 'react-redux';
import thousands from 'thousands';

import { Button } from 'components/button';
import StatusChip from 'components/status-chip';
import { useDispatch, useLanguageNavigate, useLocale, useSelector } from 'hooks';
import { dateTimeToString, pcgidToString, TranslatedText } from 'lib/utils';
import { useI18n } from 'locales';
import { STATUS_LABELS } from 'models/giving';
import PortalHeader from '../components/PortalHeader';
import SubscriptionDetailsDialog from '../subscription/components/SubscriptionDetailsDialog';
import GivingInfoBox from './components/GivingInfoBox';

const selector = state => ({
  currentInfo: state.currentInfo,
  givingsStatisticsInfo: state.givingsStatisticsInfo,
});

function PortalDashboardPage() {
  const { currentInfo, givingsStatisticsInfo } = useSelector(selector, shallowEqual);
  const dispatch = useDispatch();
  const navigate = useLanguageNavigate();
  const locale = useLocale();
  const t = useI18n();
  const giver = get(currentInfo, 'data', {});
  const statistics = get(givingsStatisticsInfo, 'data', {});
  const recentGivings = get(statistics, 'recentGivings', []);
  const momentLocale = locale === 'zh-Hans' ? 'zh-cn' : locale === 'zh-Hant' ? 'zh-hk' : 'en-gb';
  moment.locale(momentLocale);

  const [subscriptionDialogOpen, setSubscriptionDialogOpen] = useState(false);
  const [selectedSubscriptionId, setSelectedSubscriptionId] = useState(null);

  const onViewSubscription = useCallback((subscriptionId) => {
    setSelectedSubscriptionId(subscriptionId);
    setSubscriptionDialogOpen(true);
  }, []);

  const onCloseSubscriptionDialog = useCallback(() => {
    setSubscriptionDialogOpen(false);
    setSelectedSubscriptionId(null);
  }, []);

  useEffect(() => {
    dispatch(Actions.readCurrent());
    dispatch(Actions.readGivingsStatistics());
  }, [dispatch]);

  const isLoading = currentInfo.isFetching || givingsStatisticsInfo.isFetching;

  // Calculate months since first giving
  const calculateMonthsAsDonor = () => {
    return moment(giver.createdAt).fromNow();
  };

  // Calculate time since last giving
  const timeSinceLastGiving = () => {
    return moment(giver.lastGaveAt).fromNow();
  };

  const getFundName = useCallback((giving) => {
    switch (locale) {
      case 'zh-Hant':
        return get(giving, 'fund.nameChi') || get(giving, 'fund.nameEng') || '-';
      case 'zh-Hans':
        return get(giving, 'fund.nameSimplifiedChi') || get(giving, 'fund.nameEng') || '-';
      default:
        return get(giving, 'fund.nameEng') || '-';
    }
  }, [locale]);

  const getGivingType = useCallback((giving) => {
    const paymentMethod = get(giving, 'onlinePayment.paymentMethod')
      || get(giving, `paymentMethod.${locale === 'zh-Hant' ? 'nameChi' : locale === 'zh-Hans' ? 'nameChi' : 'nameEng'}`);
    return (
      <>
        <Typography variant='paragraph' color='secondary'><TranslatedText value={giving.type} /></Typography>
        {
          giving.subscription?.refId && (
            <div>
              <Typography variant='paragraph' color='info' sx={{ display: 'inline' }}>
                <TranslatedText value='Subscription Ref ID: ' />
              </Typography>
              <Link
                component="button"
                variant="body2"
                onClick={(e) => {
                  e.stopPropagation();
                  if (giving.subscriptionId) {
                    onViewSubscription(giving.subscriptionId);
                  }
                }}
                sx={{
                  fontFamily: 'monospace',
                  cursor: 'pointer',
                  textDecoration: 'underline',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 0.5,
                }}
              >
                {giving.subscription?.refId}
                <FontAwesomeIcon icon={faArrowUpRightFromSquare} size="xs" />
              </Link>
            </div>
          )
        }
        {paymentMethod && (
          <Typography variant="subtitle1" color="textSecondary">
            {paymentMethod}
          </Typography>
        )}
      </>
    );
  }, [locale, onViewSubscription]);

  return (
    <Stack sx={{
      backgroundColor: 'background.default',
    }}>

      <PortalHeader header={t('Overview')} />

      {isLoading ? (
        <Stack sx={{
          backgroundColor: 'background.default',
          alignItems: 'center',
          justifyContent: 'center',
          py: 8,
        }}>
          <CircularProgress />
        </Stack>
      ) : (
        <>
          <Stack gap={2} sx={{ px: 2, py: { xs: 2, sm: 3 } }}>
            <Stack gap={2} sx={{ display: { xs: 'flex', md: 'none' } }}>
              <Button
                component="a"
                onClick={() => navigate(`/give?locale=${locale}`)}
                variant="contained"
                color="primary"
                leading={faHandHoldingDollar}
                label={t('Giving')}
                sx={{
                  fontSize: { xs: '1.1rem', sm: '1rem' },
                  fontWeight: 'bold',
                  px: { xs: 4, sm: 3 },
                  py: { xs: 1.5, sm: 1 },
                  width: { xs: '100%' },
                  boxShadow: 3,
                  '&:hover': {
                    boxShadow: 4,
                  }
                }}
              />
            </Stack >
            <Stack gap={2} sx={{ display: 'flex', flexDirection: 'row', alignContent: 'center', justifyContent: 'space-between' }}>
              <Stack>
                <Typography variant='h4' sx={{ fontWeight: 'bold' }}>
                  {giver.lastNameEng || ''}{giver.firstNameEng ? `, ${giver.firstNameEng}` : ''}
                </Typography>
                {(giver.lastNameChi || giver.firstNameChi) && (
                  <Typography variant='h5' sx={{ fontWeight: 'medium', color: 'text.secondary' }}>
                    {giver.lastNameChi || ''}{giver.firstNameChi || ''}
                  </Typography>
                )}
              </Stack>

              <Button
                component="a"
                onClick={() => navigate(`/give?locale=${locale}`)}
                variant="contained"
                color="primary"
                leading={faHandHoldingDollar}
                label={t('Giving')}
                sx={{
                  display: { xs: 'none', md: 'inline-flex' },
                  fontSize: '1rem',
                  fontWeight: 'bold',
                  px: 3,
                }}
              />
            </Stack>

            {/* Main Summary - Only ID and Last Giving */}
            <Card sx={{ boxShadow: 2, borderRadius: 2, backgroundColor: 'primary.50', borderColor: 'primary.main' }}>
              <CardContent sx={{ py: 1, px: 2, '&:last-child': { pb: 1 } }}>
                <Stack direction={{ xs: 'column', sm: 'row' }} gap={2} divider={<Divider orientation="vertical" flexItem />}>
                  <Stack gap={1} sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', flex: 1 }}>
                    <Typography variant='h6' color='primary.main' sx={{ minWidth: '32px', textAlign: 'center', fontSize: '1.1rem' }}>
                      <FontAwesomeIcon icon={faIdCardClip} />
                    </Typography>
                    <Stack>
                      <Typography variant='caption' color='text.secondary' sx={{ fontWeight: 'bold', fontSize: '0.75rem' }}>
                        {t('PCGID')}
                      </Typography>
                      <Typography variant='h6' color='secondary.main' sx={{ fontWeight: 'bold', fontSize: '1.1rem' }}>
                        {pcgidToString(giver.pcgid) || '-'}
                      </Typography>
                    </Stack>
                  </Stack>

                  {giver.lastGaveAt && (
                    <Stack gap={1} sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', flex: 1 }}>
                      <Typography variant='h6' color='primary.main' sx={{ minWidth: '32px', textAlign: 'center', fontSize: '1.1rem' }}>
                        <FontAwesomeIcon icon={faClockRotateLeft} />
                      </Typography>
                      <Stack>
                        <Typography variant='caption' color='text.secondary' sx={{ fontWeight: 'bold', fontSize: '0.75rem' }}>
                          {t('Last Giving')}
                        </Typography>
                        <Typography variant='h6' color='secondary.main' sx={{ fontWeight: 'bold', fontSize: '1.1rem' }}>
                          {timeSinceLastGiving()}
                        </Typography>
                        <Typography variant='caption' color='text.secondary' sx={{ fontSize: '0.7rem' }}>
                          {dateTimeToString(giver.lastGaveAt)}
                        </Typography>
                      </Stack>
                    </Stack>
                  )}
                </Stack>
              </CardContent>
            </Card>

            {/* Additional Profile Information - Compact Section */}
            <Card sx={{ boxShadow: 1, borderRadius: 2 }}>
              <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
                <Stack direction={{ xs: 'column', sm: 'row' }} gap={2} divider={<Divider orientation="vertical" flexItem />}>
                  <Stack gap={0.5} sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', flex: 1 }}>
                    <Typography variant='body2' color='primary.main' sx={{ minWidth: '20px', fontSize: '0.9rem' }}>
                      <FontAwesomeIcon icon={faEnvelope} />
                    </Typography>
                    <Stack gap={0}>
                      <Typography variant='caption' color='text.secondary' sx={{ fontSize: '0.7rem' }}>
                        {t('Email')}
                      </Typography>
                      <Typography variant='body2' color='text.primary' sx={{ fontSize: '0.85rem' }}>
                        {giver.email || '-'}
                      </Typography>
                    </Stack>
                  </Stack>

                  <Stack gap={0.5} sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', flex: 1 }}>
                    <Typography variant='body2' color='primary.main' sx={{ minWidth: '20px', fontSize: '0.9rem' }}>
                      <FontAwesomeIcon icon={faPhone} />
                    </Typography>
                    <Stack gap={0}>
                      <Typography variant='caption' color='text.secondary' sx={{ fontSize: '0.7rem' }}>
                        {t('Phone')}
                      </Typography>
                      <Typography variant='body2' color='text.primary' sx={{ fontSize: '0.85rem' }}>
                        {(giver.countryCode || giver.phone) ? `${giver.countryCode ? `+${giver.countryCode} ` : ''}${giver.phone}` : '-'}
                      </Typography>
                    </Stack>
                  </Stack>

                  <Stack gap={0.5} sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', flex: 1 }}>
                    <Typography variant='body2' color='primary.main' sx={{ minWidth: '20px', fontSize: '0.9rem' }}>
                      <FontAwesomeIcon icon={faChurch} />
                    </Typography>
                    <Stack gap={0}>
                      <Typography variant='caption' color='text.secondary' sx={{ fontSize: '0.7rem' }}>
                        {t('Service')}
                      </Typography>
                      <Typography variant='body2' color='text.primary' sx={{ fontSize: '0.85rem' }}>
                        {giver.service || '-'}
                      </Typography>
                    </Stack>
                  </Stack>

                  <Stack gap={0.5} sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', flex: 1 }}>
                    <Typography variant='body2' color='primary.main' sx={{ minWidth: '20px', fontSize: '0.9rem' }}>
                      <FontAwesomeIcon icon={faHeart} />
                    </Typography>
                    <Stack gap={0}>
                      <Typography variant='caption' color='text.secondary' sx={{ fontSize: '0.7rem' }}>
                        {t('Since')}
                      </Typography>
                      <Typography variant='body2' color='text.primary' sx={{ fontSize: '0.85rem' }}>
                        {calculateMonthsAsDonor()}
                      </Typography>
                    </Stack>
                  </Stack>
                </Stack>
              </CardContent>
            </Card>
          </Stack >

          <Stack direction='row' gap={2} sx={{ px: 2 }}>
            <Stack gap={0.5} sx={{ p: 2, backgroundColor: 'white', borderRadius: 2, flex: 1, boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)' }}>
              <Stack gap={1} sx={{ display: 'flex', flexDirection: 'row', alignItems: 'flex-end' }}>
                <Typography variant='h6' color='secondary.500' sx={{ fontWeight: 'bold' }}>
                  {t('HKD')}
                </Typography>
                <Typography variant='h2' color='secondary.500' sx={{ fontWeight: 'bold' }}>
                  {thousands((statistics.totalAmount || 0).toFixed(2))}
                </Typography>
              </Stack>
              <Typography variant='paragraph' color='info'>
                {t('Total Amount (Fiscal Year To Date)')}
              </Typography>
            </Stack>
          </Stack>

          {/* Recent Givings */}
          <Stack gap={2} sx={{ px: 2, py: 3 }}>
            <Typography variant='h4' color='secondary' sx={{ fontWeight: 'bold' }}>
              {t('RECENT GIVING')}
            </Typography>

            {/* Mobile Card Layout */}
            <Stack gap={2} sx={{ display: { xs: 'flex', md: 'none' } }}>
              {isLoading && (
                <Skeleton variant="rectangular" width="100%" height={200} />
              )}
              {!isLoading && recentGivings.length > 0 ? (
                recentGivings.map((giving, index) => (
                  <Card key={`mobile-giving-${index}`} sx={{ boxShadow: 2 }}>
                    <CardContent>
                      <Stack gap={1.5}>
                        <Stack direction="row" justifyContent="space-between" alignItems="center">
                          <Typography variant='caption' color='text.secondary' sx={{ fontWeight: 'bold' }}>
                            {t('Date')}
                          </Typography>
                          <Typography variant='body2' color='info.700'>
                            {dateTimeToString(get(giving, 'date'))}
                          </Typography>
                        </Stack>
                        <Divider />
                        <Stack direction="row" justifyContent="space-between" alignItems="center">
                          <Typography variant='caption' color='text.secondary' sx={{ fontWeight: 'bold' }}>
                            {t('Amount')}
                          </Typography>
                          <Typography variant='body2' color='info.700' sx={{ textAlign: 'right' }}>
                            HKD {thousands((get(giving, 'amount') || 0).toFixed(2))}
                          </Typography>
                        </Stack>
                        <Divider />
                        <Stack direction="row" justifyContent="space-between" alignItems="center">
                          <Typography variant='caption' color='text.secondary' sx={{ fontWeight: 'bold' }}>
                            {t('Fund')}
                          </Typography>
                          <Typography variant='body2' color='info.700' sx={{ textAlign: 'right' }}>
                            {getFundName(giving)}
                          </Typography>
                        </Stack>
                        <Divider />
                        <Stack direction="row" justifyContent="space-between" alignItems="center">
                          <Typography variant='caption' color='text.secondary' sx={{ fontWeight: 'bold' }}>
                            {t('Type')}
                          </Typography>
                          <Box sx={{ textAlign: 'right' }}>
                            {getGivingType(giving)}
                          </Box>
                        </Stack>
                        <Divider />
                        <Stack direction="row" justifyContent="space-between" alignItems="center">
                          <Typography variant='caption' color='text.secondary' sx={{ fontWeight: 'bold' }}>
                            {t('Status')}
                          </Typography>
                          <Box>
                            {(() => {
                              const status = get(giving, 'status');
                              const statusLabel = typeof status === 'number' ? STATUS_LABELS[status] : status;
                              return statusLabel && <StatusChip status={statusLabel} />;
                            })()}
                          </Box>
                        </Stack>
                        <Divider />
                        <Stack direction="row" justifyContent="space-between" alignItems="center">
                          <Typography variant='caption' color='text.secondary' sx={{ fontWeight: 'bold' }}>
                            {t('Ref ID')}
                          </Typography>
                          <Typography variant='body2' color='info.700'>
                            {get(giving, 'refId')}
                          </Typography>
                        </Stack>
                      </Stack>
                    </CardContent>
                  </Card>
                ))
              ) : (
                !isLoading && (
                  <Card sx={{ boxShadow: 2 }}>
                    <CardContent>
                      <Typography variant='body2' color='info.700' sx={{ textAlign: 'center', py: 2 }}>
                        {t('No recent giving found')}
                      </Typography>
                    </CardContent>
                  </Card>
                )
              )}
            </Stack>

            {/* Desktop Table Layout */}
            <TableContainer component={Paper} sx={{ display: { xs: 'none', md: 'block' }, boxShadow: 3, overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
              <Table sx={{ minWidth: 760 }}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                      <Typography variant='subtitle1' color='text.primary' sx={{ fontWeight: 'bold' }}>
                        {t('Date')}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                      <Typography variant='subtitle1' color='text.primary' sx={{ fontWeight: 'bold' }}>
                        {t('Amount')}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                      <Typography variant='subtitle1' color='text.primary' sx={{ fontWeight: 'bold' }}>
                        {t('Fund')}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                      <Typography variant='subtitle1' color='text.primary' sx={{ fontWeight: 'bold' }}>
                        {t('Type')}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                      <Typography variant='subtitle1' color='text.primary' sx={{ fontWeight: 'bold' }}>
                        {t('Status')}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                      <Typography variant='subtitle1' color='text.primary' sx={{ fontWeight: 'bold' }}>
                        {t('Ref ID')}
                      </Typography>
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {isLoading && (
                    <Skeleton variant="rectangular" width="100%" height={200} />
                  )}
                  {recentGivings.length > 0 ? (
                    recentGivings.map((giving, index) => (
                      <GivingInfoBox
                        key={`donation-info-box-${index}`}
                        giving={giving}
                        onViewSubscription={onViewSubscription}
                      />
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={5} align="center">
                        <Typography variant='paragraph' color='info.700' sx={{ py: 2 }}>
                          No recent giving found
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>

          </Stack>
        </>
      )
      }

      <SubscriptionDetailsDialog
        open={subscriptionDialogOpen}
        onClose={onCloseSubscriptionDialog}
        subscriptionId={selectedSubscriptionId}
      />
    </Stack >
  );
}

export default PortalDashboardPage;
